// v2 boulder — one big rounded mass of weathered grey stone: a great dome with a
// lower shoulder grown into it and rubble strewn at the foot. Variant 0 is the
// whole mass, variant 1 is visibly split (a calved slice leans away over a dark
// cleft) and variant 2 is a lower, wind-worn dome with a capstone perched on top.
// Ported 1:1 from age-of-mcp's v2/boulder.ts, using the library's shared
// `mineral` (scree) helper.
import * as THREE from "three";
import { defineModel, mineral, type Part } from "../../lib";

function boulderParts(v: number): Part[] {
  const tint = ["#7e828a", "#888b91", "#74777e"][v % 3]!;
  const shade = "#696c74";
  const parts: Part[] = [];

  if (v === 1) {
    // split boulder — the dome plus a calved slice leaning away
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.46, 0), color: tint,
        position: [-0.12, 0.32, 0], rotation: [0, 0.45, 0.06],
        scale: [1.0, 0.92, 1.05] },
      // the slice that broke off, tipped outward
      { geometry: new THREE.IcosahedronGeometry(0.36, 0), color: shade,
        position: [0.33, 0.26, 0.02], rotation: [0.05, 1.2, -0.22],
        scale: [0.55, 0.9, 0.95] },
      // the dark cleft between the halves
      { geometry: new THREE.BoxGeometry(0.05, 0.5, 0.66), color: "#3e4147",
        position: [0.14, 0.28, 0], rotation: [0, 0.1, -0.1] },
    );
  } else if (v === 2) {
    // weathered — lower and wider, a capstone perched on the crown
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.5, 0), color: tint,
        position: [0, 0.3, 0], rotation: [0, 0.8, 0],
        scale: [1.28, 0.78, 1.14] },
      { geometry: new THREE.IcosahedronGeometry(0.16, 0), color: shade,
        position: [0.03, 0.66, -0.04], rotation: [0.3, 1.7, 0.15],
        scale: [1.1, 0.75, 1.0] },
    );
  } else {
    // the whole mass — a great dome with a shoulder grown into it
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.5, 0), color: tint,
        position: [-0.04, 0.33, 0], rotation: [0, 0.3, 0.04],
        scale: [1.16, 0.9, 1.06] },
      { geometry: new THREE.IcosahedronGeometry(0.33, 0), color: shade,
        position: [0.3, 0.2, 0.1], rotation: [0.1, 1.5, 0],
        scale: [1.0, 0.85, 0.95] },
    );
  }

  // rubble at the foot
  parts.push(...mineral.scree(v * 11 + 3, 3, [0.5, 0.62], [0.06, 0.1],
    [tint, shade, "#8d9097"]));
  return parts;
}

export const boulderV2 = defineModel({
  id: "boulder-v2",
  batchId: "fable",
  name: "Boulder (v2)",
  tags: ["scenery","boulder","low-poly","age-of-mcp","fable"],
  summary: "Low-poly boulder.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.55,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => boulderParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => boulderParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => boulderParts(2) },
  ],
  animations: [],
});
